import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, X, AlertTriangle, Calendar, Leaf } from 'lucide-react'
import { getNotifications, getNotificationCount, markNotificationRead, markAllNotificationsRead } from '../services/api'

const PAGE_SIZE = 20

export default function NotificationBell({ ranchId, userId }) {
  const [isOpen, setIsOpen] = useState(false)
  const [offset, setOffset] = useState(0)
  const [allItems, setAllItems] = useState([])
  const queryClient = useQueryClient()

  // Fetch notification count
  const { data: countData } = useQuery({
    queryKey: ['notificationCount', ranchId, userId],
    queryFn: () => getNotificationCount(ranchId, userId),
    refetchInterval: 60000,
  })

  // Fetch notifications when dropdown is open
  const { isLoading } = useQuery({
    queryKey: ['notifications', ranchId, userId, offset],
    queryFn: async () => {
      const data = await getNotifications(ranchId, userId, false, PAGE_SIZE, offset)
      setAllItems(prev => offset === 0 ? data.items : [...prev, ...data.items])
      return data
    },
    enabled: isOpen,
  })

  // Get latest page data for has_more
  const latestPage = queryClient.getQueryData(['notifications', ranchId, userId, offset])
  const hasMore = latestPage?.has_more ?? false

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: (_, notificationId) => {
      setAllItems(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n))
      queryClient.invalidateQueries(['notificationCount'])
    },
  })

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(ranchId, userId),
    onSuccess: () => {
      setAllItems(prev => prev.map(n => ({ ...n, is_read: true })))
      queryClient.invalidateQueries(['notificationCount'])
    },
  })

  const handleOpen = () => {
    if (!isOpen) {
      setOffset(0)
      setAllItems([])
    }
    setIsOpen(!isOpen)
  }

  const getIcon = (type) => {
    switch (type) {
      case 'grazing_calendar_reminder':
        return <Calendar className="h-4 w-4 text-blue-500" />
      case 'lantana_clearing_reminder':
        return <Leaf className="h-4 w-4 text-green-500" />
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      default:
        return <Bell className="h-4 w-4 text-gray-500" />
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-4 border-red-500 bg-red-50'
      case 'high':
        return 'border-l-4 border-orange-500 bg-orange-50'
      default:
        return 'border-l-4 border-gray-200'
    }
  }

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
      >
        <Bell className="h-6 w-6" />
        {countData?.unread > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {countData.unread > 99 ? '99+' : countData.unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {countData?.unread > 0 && (
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && allItems.length === 0 ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : allItems.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications</p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-gray-100">
                  {allItems.map((notification) => (
                    <li
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 cursor-pointer ${
                        !notification.is_read ? getPriorityColor(notification.priority) : ''
                      }`}
                      onClick={() => {
                        if (!notification.is_read) {
                          markReadMutation.mutate(notification.id)
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getIcon(notification.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            notification.is_read ? 'text-gray-600' : 'text-gray-900'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {hasMore && (
                  <button
                    onClick={() => setOffset(prev => prev + PAGE_SIZE)}
                    disabled={isLoading}
                    className="w-full py-3 text-sm text-blue-600 hover:text-blue-800 hover:bg-gray-50 border-t disabled:opacity-50"
                  >
                    {isLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}
